import React, {useContext} from 'react';
import {useCallback} from 'react';
import useLocalStorage from 'hooks/useLocalStorage';

const Context = React.createContext({
  tariffState: {},
  squareMeters: 1,

  checkIsChosen: () => {},
  getSupplements: () => {},
  addSupplement: () => {},
  removeSupplement: () => {},

  setSquareMeters: () => {},

  purge: () => {},
});

const TariffProvider = ({children}) => {
  const [storedTariffState, setStoredTariffState] = useLocalStorage('tariff');
  const tariffState = storedTariffState || {};

  const getSupplements = useCallback(
    tariff => {
      if (!tariff) {
        return [];
      }

      return tariffState[tariff]?.supplements || [];
    },
    [tariffState],
  );

  const checkIsChosen = useCallback(
    (supplement, tariff) => {
      const storedSupplements = tariffState[tariff]?.supplements || [];

      if (storedSupplements?.length) {
        return storedSupplements
          .map(storedSupplement => storedSupplement.id)
          .includes(supplement.id);
      }

      return false;
    },
    [tariffState],
  );

  const addSupplement = useCallback(
    (supplement, tariff) => {
      const storedSupplements = tariffState[tariff]?.supplements || [];

      if (storedSupplements?.length) {
        setStoredTariffState({
          ...tariffState,
          [tariff]: {supplements: [...storedSupplements, supplement]},
        });
      } else {
        setStoredTariffState({
          ...tariffState,
          [tariff]: {supplements: [supplement]},
        });
      }
    },
    [tariffState],
  );

  const removeSupplement = useCallback(
    (supplement, tariff) => {
      const storedSupplements = tariffState[tariff]?.supplements || [];

      if (storedSupplements?.length) {
        setStoredTariffState({
          ...tariffState,
          [tariff]: {
            supplements: tariffState[tariff].supplements.filter(
              storedSupplement => storedSupplement.name !== supplement.name,
            ),
          },
        });
      } else {
        setStoredTariffState({
          ...tariffState,
          [tariff]: {supplements: []},
        });
      }
    },
    [tariffState],
  );

  const setSquareMeters = useCallback(payload => {
    setStoredTariffState({...tariffState, squareMeters: payload});
  });

  const purge = useCallback(tariff => {
    setStoredTariffState({...tariffState, [tariff]: {supplements: []}});
  });

  return (
    <Context.Provider
      value={{
        tariffState,
        checkIsChosen,
        getSupplements,
        addSupplement,
        removeSupplement,
        setSquareMeters,
        purge,
      }}
    >
      {children}
    </Context.Provider>
  );
};

const TariffProviderWrapper = ({children}) => {
  return <TariffProvider>{children}</TariffProvider>;
};

export function useTariff() {
  const context = useContext(Context);

  return {
    tariffState: context.tariffState,
    checkIsChosen: context.checkIsChosen,
    getSupplements: context.getSupplements,
    addSupplement: context.addSupplement,
    removeSupplement: context.removeSupplement,
    setSquareMeters: context.setSquareMeters,
    purge: context.purge,
  };
}

export default {
  Context,
  Consumer: Context.Consumer,
  Provider: TariffProviderWrapper,
};
